import { useTranslation } from "react-i18next";
import { PublicInvoiceDetails } from "../data/invoice";
import type { PaymentResultStatus } from "./payment-form";
import { isColorLight, getContrastColor } from "../utils/colors";
import { formatCurrency } from "../utils/format";
import Header from "./header";

interface PaymentSuccessProps {
  invoiceData: PublicInvoiceDetails;
  // When `processing`, the payment is not yet captured — show an accurate
  // "payment processing" state instead of a paid confirmation.
  status?: PaymentResultStatus;
}

function PaymentSuccess({
  invoiceData,
  status = "succeeded",
}: PaymentSuccessProps) {
  const { t, i18n } = useTranslation();
  const isProcessing = status === "processing";
  // Primary (brandColor) is used for buttons, links, and accents
  // Secondary (secondaryColor) is used for the page background
  const brandColor = invoiceData.business.brandColor || "#eb5e27";
  const secondaryColor = invoiceData.business.secondaryColor || "#FFF7F0";
  const businessName = invoiceData.business.name || "";

  // Determine text colors based on background brightness
  const isLightBg = isColorLight(secondaryColor);
  const textColor = getContrastColor(secondaryColor);
  const textMutedColor = isLightBg
    ? "rgba(0,0,0,0.6)"
    : "rgba(255,255,255,0.7)";

  return (
    <div className="min-h-screen" style={{ backgroundColor: secondaryColor }}>
      <div className="absolute left-0 right-0 top-0">
        <Header
          logo={invoiceData.business.logo}
          businessName={businessName}
          textColor={textColor}
        />
      </div>

      <div className="flex min-h-screen flex-col items-center justify-center px-4 py-20">
        {/* Success card */}
        <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-xl">
          {/* Success icon */}
          <div className="mb-6 text-center">
            <div
              className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
              style={{ backgroundColor: `${brandColor}15` }}
            >
              {isProcessing ? (
                <svg
                  className="h-8 w-8"
                  style={{ color: brandColor }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              ) : (
                <svg
                  className="h-8 w-8"
                  style={{ color: brandColor }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">
              {isProcessing
                ? t("gymkeePay.paymentSuccess.processingTitle")
                : t("gymkeePay.paymentSuccess.title")}
            </h2>
            <p className="mt-2 text-gray-600">
              {isProcessing
                ? t("gymkeePay.paymentSuccess.processingMessage")
                : t("gymkeePay.paymentSuccess.thankYou")}
            </p>
          </div>

          {/* Amount - prominent display */}
          <div className="mb-6 rounded-lg bg-gray-50 p-4 text-center">
            <p className="text-sm text-gray-500">
              {isProcessing
                ? t("gymkeePay.paymentSuccess.amountProcessing")
                : t("gymkeePay.paymentSuccess.amountPaid")}
            </p>
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(
                invoiceData.invoice.amountDue,
                invoiceData.invoice.currency,
              )}
            </p>
          </div>

          {/* Payment details */}
          <div className="space-y-3 border-t border-gray-200 pt-4">
            {invoiceData.invoice.number && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">
                  {t("gymkeePay.paymentSuccess.invoice")}
                </span>
                <span className="font-medium text-gray-900">
                  #{invoiceData.invoice.number}
                </span>
              </div>
            )}
            {invoiceData.customer.name && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">
                  {t("gymkeePay.paymentSuccess.paidBy")}
                </span>
                <span className="font-medium text-gray-900">
                  {invoiceData.customer.name}
                </span>
              </div>
            )}
            {invoiceData.customer.email && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">
                  {t("gymkeePay.paymentSuccess.receiptSentTo")}
                </span>
                <span className="font-medium text-gray-900">
                  {invoiceData.customer.email}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">
                {t("gymkeePay.paymentSuccess.date")}
              </span>
              <span className="font-medium text-gray-900">
                {new Date().toLocaleDateString(i18n.language, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>
          </div>

          {invoiceData.invoice.invoicePdf && (
            <a
              href={invoiceData.invoice.invoicePdf}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 block w-full rounded-lg px-6 py-3 text-center font-semibold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: brandColor }}
            >
              {t("gymkeePay.invoice.downloadReceipt", {
                defaultValue: "Download my receipt",
              })}
            </a>
          )}

          {/* Subscription confirmation — only once the payment is captured.
              While processing, the subscription is not yet active. */}
          {invoiceData.subscription && !isProcessing && (
            <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-start gap-3">
                <svg
                  className="mt-0.5 h-5 w-5 shrink-0 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-green-900">
                    {t("gymkeePay.paymentSuccess.subscriptionActivated")}
                  </p>
                  <p className="mt-0.5 text-sm text-green-700">
                    {t("gymkeePay.paymentSuccess.subscriptionActiveMessage", {
                      frequency:
                        invoiceData.subscription.intervalCount > 1
                          ? `${invoiceData.subscription.intervalCount} ${t(`gymkeePay.interval.${invoiceData.subscription.interval}`)}`
                          : t(
                              `gymkeePay.frequency.${invoiceData.subscription.interval === "month" ? "monthly" : invoiceData.subscription.interval === "year" ? "yearly" : invoiceData.subscription.interval === "week" ? "weekly" : "daily"}`,
                            ),
                      productName: invoiceData.subscription.productName,
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Contact info */}
          {invoiceData.business.supportEmail && (
            <div className="mt-6 border-t border-gray-200 pt-4 text-center">
              <p className="text-sm text-gray-500">
                {t("gymkeePay.paymentSuccess.questionsAboutPayment")}{" "}
                <a
                  href={`mailto:${invoiceData.business.supportEmail}`}
                  className="font-medium hover:underline"
                  style={{ color: brandColor }}
                >
                  {t("gymkeePay.paymentSuccess.contactBusiness", {
                    businessName,
                  })}
                </a>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm" style={{ color: textMutedColor }}>
            {t("gymkeePay.footer.poweredBy")}{" "}
            <a
              href="https://gymkee.io"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:underline"
              style={{ color: textColor }}
            >
              Gymkee
            </a>
            , {t("gymkeePay.footer.securedBy")}{" "}
            <span className="font-medium" style={{ color: textColor }}>
              Stripe
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccess;
